import { useState } from 'react';

import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';

import { ErrorMessage } from '../../../../components/ErrorMessage/ErrorMessage';
import { AppLogo } from '../../../../components/SVGIcons/AppLogo/AppLogo';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { useStores } from '../../../../stores/useStores';
import { getErrorMessage } from '../../../../utils/errors';

import classes from './EmailVerificationLanding.module.scss';
import envelope from './envelope.webp';

interface EmailVerificationLandingProps {
  onVerificationComplete: () => void;
}

export const EmailVerificationLanding = observer(({ onVerificationComplete }: EmailVerificationLandingProps) => {
  const { t } = useTranslation();
  const { authStore, landingStore } = useStores();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [error, setError] = useState<string>('');
  const { sendVerificationEmailCooldown } = authStore;

  const handleResend = async () => {
    try {
      setError('');
      await authStore.resendEmailVerification();
    } catch (e) {
      const msg = getErrorMessage(e);
      setError(msg);
    }
  };

  useEffectOnce(() => {
    landingStore.sendLandingPageEvent('direct_reg_email_verification');

    const unsub = authStore.trackEmailVerificationCompletion(() => {
      onVerificationComplete();
    });

    return () => {
      unsub();
      authStore.clearSendVerificationEmailCooldown();
    };
  });

  return (
    <div className={classes.emailVerificationLandingContainer}>
      <div className={classes.emailVerificationLanding}>
        <div className={classes.logoSection}>
          <AppLogo
            alwaysColorful
            withText
            textClassname={classes.logoText}
            textColor="#fff"
            width={isMobile ? 58 : 82}
          />
        </div>

        <div className={classes.contentSection}>
          <div className={classes.title} data-testid="email-verification-title">
            {t('auth.checkYourEmail')}!
          </div>

          <div className={classes.subtitle}>
            {t('emailVerification.verifyYourAccount')}
            <br />
            {t('emailVerification.getyour')}
            <span className={classes.welcomeBonus}> {t('emailVerification.getWelcomeBonusSuffix')}</span>
          </div>

          <div className={classes.envelopeContainer}>
            <img alt="envelope" className={classes.envelopeImage} src={envelope} />
          </div>

          <div className={classes.emailSentMessage}>
            {t('emailVerification.confirmationEmailSent')}{' '}
            <span className={classes.emailAddress}>{authStore.currentUserEmail}</span>
          </div>

          <div className={classes.instructionMessage} data-testid="direct-email-check-spam">
            <Trans components={{ break: <br /> }} i18nKey="landing.direct.emailVerification.checkSpamMessage" />
          </div>

          <div className={classes.resendSection} data-testid="direct-email-resend-section">
            {sendVerificationEmailCooldown > 0 ? (
              <Trans
                components={{
                  cooldown: <span className={classes.resendDisabled} />,
                  prompt: <span className={classes.noEmailText} />,
                }}
                i18nKey="landing.direct.emailVerification.didntReceiveCooldownSentence"
                values={{ seconds: sendVerificationEmailCooldown }}
              />
            ) : (
              <Trans
                components={{
                  prompt: <span className={classes.noEmailText} />,
                  resend: <span className={classes.resendLink} onClick={handleResend} />,
                }}
                i18nKey="landing.direct.emailVerification.didntReceiveResendSentence"
              />
            )}
          </div>

          <ErrorMessage noSpaceWhenEmpty className={classes.errorMessage}>
            {error}
          </ErrorMessage>
        </div>
      </div>
    </div>
  );
});
